import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Children, Component } from 'react';
import PropTypes from 'prop-types';
import { themeable } from '@instructure/ui-themeable';
import { controllable, Children as ChildrenPropTypes } from '@instructure/ui-prop-types';
import { omitProps, safeCloneElement, matchComponentTypes } from '@instructure/ui-react-utils';
import { uid } from '@instructure/uid';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
import { testable } from '@instructure/ui-testable';
import { MenuItem } from '../MenuItem';
import { MenuItemSeparator } from '../MenuItemSeparator';
var styles = {
  componentId: 'eoNrR',
  template: function template(theme) {
    return "\n\n.eoNrR_bGBk{display:block}\n\n.eoNrR_JelF{list-style-type:none;margin:0 0 0 0;padding:0}\n\n.eoNrR_blJt{background:".concat(theme.background || 'inherit', ";color:").concat(theme.color || 'inherit', ";display:block;font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.fontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";padding:").concat(theme.padding || 'inherit', ";text-align:start}\n\n[dir=ltr] .eoNrR_blJt{text-align:left}\n\n[dir=rtl] .eoNrR_blJt{text-align:right}");
  },
  'root': 'eoNrR_bGBk',
  'items': 'eoNrR_JelF',
  'label': 'eoNrR_blJt'
};
import theme from './theme';
/**
---
parent: Menu
id: Menu.Group
---
**/

var MenuItemGroup = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 =
/*#__PURE__*/
function (_Component) {
  _inherits(MenuItemGroup, _Component);

  function MenuItemGroup(props) {
    var _this;

    _classCallCheck(this, MenuItemGroup);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(MenuItemGroup).call(this));

    _this.handleSelect = function (e, value, selected, item) {
      if (_this.props.disabled) {
        e.preventDefault();
        return;
      }

      if (_this.props.selected) {
        _this.updateSelected(e, value, _this.props.selected, selected, item);
      } else {
        _this.setState(function (state) {
          return {
            selected: _this.updateSelected(e, value, state.selected, selected, item)
          };
        });
      }
    };

    _this.updateSelected = function (e, value, items, selected, item) {
      var allowMultiple = _this.props.allowMultiple;
      var updated = allowMultiple ? _toConsumableArray(items) : [];
      var location = updated.indexOf(value);

      if (selected === true && location < 0) {
        updated.push(value);
      } else if (selected === false && location !== -1) {
        updated.splice(location, 1);
      } else if (!allowMultiple && updated.length < 1) {
        // don't allow nothing selected if it's not allowMultiple/checkbox
        updated = _toConsumableArray(items);
      }

      if (typeof _this.props.onSelect === 'function') {
        _this.props.onSelect(e, updated, selected, item);
      }

      return updated;
    };

    if (typeof props.selected === 'undefined') {
      _this.state = {
        selected: _this.selectedFromChildren(props) || props.defaultSelected
      };
    }

    _this._labelId = uid('MenuItemGroup');
    return _this;
  }

  _createClass(MenuItemGroup, [{
    key: "selectedFromChildren",
    value: function selectedFromChildren(props) {
      var children = props.children,
          allowMultiple = props.allowMultiple;
      var selected = [];
      var items = Children.toArray(children).filter(function (child) {
        return matchComponentTypes(child, [MenuItem]);
      });
      items.forEach(function (item, index) {
        if ((selected.length === 0 || allowMultiple) && (item.props.selected || item.props.defaultSelected)) {
          selected.push(item.props.value || index);
        }
      });
      return selected.length > 0 ? selected : null;
    }
  }, {
    key: "renderLabel",
    value: function renderLabel() {
      var label = this.props.label;
      return hasVisibleChildren(label) ? React.createElement("span", {
        className: styles.label
      }, label) : label;
    }
  }, {
    key: "renderChildren",
    value: function renderChildren() {
      var _this2 = this;

      var _this$props = this.props,
          children = _this$props.children,
          disabled = _this$props.disabled,
          controls = _this$props.controls,
          allowMultiple = _this$props.allowMultiple,
          isTabbable = _this$props.isTabbable,
          onMouseOver = _this$props.onMouseOver;
      var index = -1;
      return Children.map(children, function (child) {
        if (matchComponentTypes(child, [MenuItem])) {
          ++index;
          var value = child.props.value || index;
          return React.createElement("li", {
            role: "none"
          }, " ", safeCloneElement(child, {
            tabIndex: isTabbable && index === 0 ? 0 : -1,
            controls: controls,
            value: value,
            type: allowMultiple ? 'checkbox' : 'radio',
            ref: _this2.props.itemRef,
            disabled: disabled || child.props.disabled,
            selected: _this2.selected.indexOf(value) > -1,
            onSelect: _this2.handleSelect,
            onMouseOver: onMouseOver
          }), " ");
        } else {
          return child;
        }
      });
    }
  }, {
    key: "render",
    value: function render() {
      var props = omitProps(this.props, MenuItemGroup.propTypes);
      return React.createElement("span", Object.assign({}, props, {
        className: styles.root,
        role: "presentation"
      }), React.createElement("span", {
        id: this._labelId
      }, this.renderLabel()), React.createElement("ul", {
        role: "menu",
        className: styles.items,
        "aria-disabled": this.props.disabled ? 'true' : null,
        "aria-labelledby": this._labelId
      }, this.renderChildren()));
    }
  }, {
    key: "selected",
    get: function get() {
      if (typeof this.props.selected === 'undefined' && typeof this.state.selected === 'undefined') {
        return [];
      } else {
        return typeof this.props.selected === 'undefined' ? _toConsumableArray(this.state.selected) : _toConsumableArray(this.props.selected);
      }
    }
  }]);

  MenuItemGroup.displayName = "MenuItemGroup";
  return MenuItemGroup;
}(Component), _class2.propTypes = {
  label: PropTypes.node.isRequired,
  allowMultiple: PropTypes.bool,

  /**
  * children of type `Menu.Item`, `Menu.Separator`
  */
  children: ChildrenPropTypes.oneOf([MenuItem, MenuItemSeparator]),

  /**
  * an array of the values (or indeces by default) for the selected items
  */
  selected: controllable(PropTypes.array, 'onSelect', 'defaultSelected'),

  /**
  * an array of the values (or indeces by default) for the selected items on initial render
  */
  defaultSelected: PropTypes.array,

  /**
  * call this function when a menu item is selected
  */
  onSelect: PropTypes.func,
  onMouseOver: PropTypes.func,
  onKeyDown: PropTypes.func,

  /**
  * the id of the element that the menu items will act upon
  */
  controls: PropTypes.string,

  /**
  * returns a reference to the `MenuItem`
  */
  itemRef: PropTypes.func,
  disabled: PropTypes.bool,

  /**
  * should the group appear in the tab order (the first item will have a tabIndex of 0)
  */
  isTabbable: PropTypes.bool
}, _class2.defaultProps = {
  onMouseOver: void 0,
  disabled: false,
  controls: void 0,
  onKeyDown: void 0,
  selected: void 0,
  children: null,
  isTabbable: false,
  allowMultiple: false,
  defaultSelected: [],
  itemRef: function itemRef(item) {},
  onSelect: function onSelect(e, value, selected, item) {}
}, _temp)) || _class) || _class);
export default MenuItemGroup;
export { MenuItemGroup };